import React, { useEffect, useState } from "react";
import { ToastContainer, toast } from "react-toastify";
import axios from "axios";
import Loader from "../components/Loader";

const BASE_URL = process.env.REACT_APP_BASE_URL;
const apiKey = process.env.REACT_APP_API_KEY;

export default function Payments() {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showModal, setShowModal] = useState(false);
  const [modalData, setModalData] = useState(null);
  const [refundLoading, setRefundLoading] = useState({});
  const [stripeStatus, setStripeStatus] = useState({});

  // Fetch all payments
  useEffect(() => {
    axios
      .get(`${BASE_URL}/getpayments`, {
        headers: { Authorization: apiKey, "Content-Type": "application/json" },
      })
      .then((res) => {
        const sorted = res.data.sort((a, b) => {
          const aTime = a.createdAt?._seconds || 0;
          const bTime = b.createdAt?._seconds || 0;
          return bTime - aTime;
        });
        setPayments(sorted);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        toast.error("Failed to load payments");
        setLoading(false);
      });
  }, []);

  // Search filter
  const searchTable = () => {
    const input = document.getElementById("search");
    const filter = input.value.toLowerCase();
    const rows = document.querySelectorAll("#PaymentTable tbody tr");

    rows.forEach((row) => {
      const match = [...row.getElementsByTagName("td")].some((cell) =>
        cell.textContent.toLowerCase().includes(filter)
      );
      row.style.display = match ? "" : "none";
    });
  };

  // Open Modal + Check Stripe Status
  const openRefundModal = async (payment) => {
    setModalData(payment);
    setShowModal(true);

    try {
      const res = await axios.get(
        `${BASE_URL}/check-stripe-status?transactionId=${payment.transactionId}`,
        { headers: { Authorization: apiKey } }
      );

      setStripeStatus((prev) => ({
        ...prev,
        [payment.transactionId]: res.data.refunded, // true/false
      }));
    } catch (err) {
      console.error("Stripe check error:", err);
      toast.error("Failed to verify Stripe refund status");
    }
  };

  // Approve/Reject refund
  const handleRefundDecision = async (decision) => {
    try {
      setRefundLoading((prev) => ({ ...prev, [modalData.transactionId]: true }));

      await axios.post(
        `${BASE_URL}/updaterefundstatus`,
        {
          transactionId: modalData.transactionId,
          status: decision,
        },
        { headers: { Authorization: apiKey, "Content-Type": "application/json" } }
      );

      // Update UI
      setPayments((prev) =>
        prev.map((p) =>
          p.transactionId === modalData.transactionId
            ? { ...p, refundRequest: { ...p.refundRequest, status: decision } }
            : p
        )
      );

      toast.success(`Refund ${decision}!`);
      setShowModal(false);
    } catch (err) {
      console.error(err);
      toast.error("Failed to update refund status");
    } finally {
      setRefundLoading((prev) => ({ ...prev, [modalData.transactionId]: false }));
    }
  };

  if (loading) return <Loader />;

  return (
    <div className="container">
      <ToastContainer position="top-center" />
      <h2>All Payment Records</h2>
      <br />
      <div className="row">
        <div className="col-sm-3">
          <input
            type="text"
            id="search"
            className="form-control"
            placeholder="Search payments..."
            onKeyUp={searchTable}
          />
        </div>
      </div>
      <br />
      <table className="table table-striped table-light" id="PaymentTable">
        <thead>
          <tr>
            <th>Athlete Name</th>
            <th>Email</th>
            <th>Sport</th>
            <th>Event</th>
            <th>Amount</th>
            <th>Status</th>
            <th>Transaction ID</th>
            <th>Refund</th>
            <th>Created At</th>
          </tr>
        </thead>
        <tbody>
          {payments.map((p) => {
            const createdTime = p.createdAt?._seconds
              ? new Date(p.createdAt._seconds * 1000).toLocaleString()
              : "N/A";

            return (
              <tr key={p.transactionId}>
                <td>{p.athleteName}</td>
                <td>{p.email}</td>
                <td>{p.sport}</td>
                <td>{p.eventName}</td>
                <td>${p.amount}</td>
                <td>
                  <span
                    className={`badge ${
                      p.status === "succeeded" ? "bg-success" : "bg-danger"
                    }`}
                  >
                    {p.status}
                  </span>
                </td>
                <td style={{ maxWidth: "180px", overflowWrap: "break-word" }}>
                  {p.transactionId}
                </td>

                {/* Refund Toggle */}
                <td>
                  <div className="form-check form-switch">
                    <input
                      className={`form-check-input ${
                        p.refundRequest?.status === "approved"
                          ? "bg-success"
                          : p.refundRequest?.status === "rejected"
                          ? "bg-danger"
                          : ""
                      }`}
                      type="checkbox"
                      checked={p.refundRequest?.status === "pending"}
                      disabled={p.refundRequest?.status === ""}
                      onClick={() => openRefundModal(p)}
                    />
                  </div>
                </td>
                <td>{createdTime}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {/* Refund Modal */}
      {showModal && modalData && (
        <div className="modal show" style={{ display: "block", background: "rgba(0,0,0,0.5)" }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Refund Request Details</h5>
                <button className="btn-close" onClick={() => setShowModal(false)}></button>
              </div>
              <div className="modal-body">
                {modalData.refundRequest && (
                  <>
                    <p><strong>Reason:</strong> {modalData.refundRequest.reason}</p>
                    <p><strong>Message:</strong> {modalData.refundRequest.message}</p>
                    <p>
                      <strong>Requested At:</strong>{" "}
                      {modalData.refundRequest.requestedAt?._seconds
                        ? new Date(modalData.refundRequest.requestedAt._seconds * 1000).toLocaleString()
                        : "N/A"}
                    </p>
                    <p><strong>Status:</strong> {modalData.refundRequest.status}</p>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-success"
                  onClick={() => handleRefundDecision("approved")}
                  disabled={
                    refundLoading[modalData.transactionId] ||
                    modalData.refundRequest?.status !== "pending" ||
                    !stripeStatus[modalData.transactionId]
                  }
                >
                  Refunded
                </button>
                {console.log(stripeStatus)}

                <button
                  className="btn btn-danger"
                  onClick={() => handleRefundDecision("rejected")}
                  disabled={
                    refundLoading[modalData.transactionId] ||
                    modalData.refundRequest?.status !== "pending"
                  }
                >
                  Denied
                </button>

                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
